import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Eye, Lightbulb, Shuffle, HelpCircle } from 'lucide-react';
import { cn } from './ClueCardSimple';
import Header from './Header';
import SEO from './SEO';
import { useSyllo } from '../context/SylloContext';
import NotFoundPage from './NotFoundPage';

const SylloCluePage = ({ isToday, date: propDate }) => {
  const params = useParams();
  const { puzzles, loading } = useSyllo();
  
  const [activeHint, setActiveHint] = useState(null);
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);
  const [revealedTiles, setRevealedTiles] = useState([]);

  const slug = params.slug;
  
  let puzzle = null;
  if (isToday) {
      puzzle = puzzles.find(p => p.date === propDate);
  } else {
      const numberMatch = params.puzzleId ? params.puzzleId.match(/syllo-(-?\d+)/) : null;
      if (numberMatch) {
          const number = numberMatch[1];
          puzzle = puzzles.find(p => p.puzzleNumber === number);
      } else {
           puzzle = puzzles.find(p => p.date === params.date);
      }
  }

  if (!puzzle) return <NotFoundPage />;
  const date = puzzle.date;

  const clue = puzzle.clues.find(c => c.slug === slug);
  if (!clue) return <NotFoundPage />;

  // Get related clues (same puzzle, excluding current one)
  const relatedClues = puzzle.clues.filter(c => c.id !== clue.id);

  const toggleHint = (key) => {
    setActiveHint(activeHint === key ? null : key);
  };

  const toggleTile = (index) => {
    if (isAnswerRevealed) return;
    if (!revealedTiles.includes(index)) {
      setRevealedTiles([...revealedTiles, index]);
    }
  };

  const answerLength = clue.answer.length;
  const tiles = Array.from({ length: answerLength });

  const backLink = isToday ? "/todays-syllo-clues-answers" : `/syllo/syllo-${puzzle.puzzleNumber}`;

  const breadcrumbs = [
    { name: "Home", item: window.location.origin },
    { name: "Syllo", item: `${window.location.origin}/syllo-archive` },
    { name: puzzle.title, item: `${window.location.origin}${backLink}` },
    { name: clue.clue_text || `Clue ${clue.number}`, item: window.location.href }
  ];

  const HintItem = ({ label, subLabel, icon: Icon, content, isOpen, onClick }) => (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mb-6">
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-blue/10 text-brand-blue">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <span className="block font-bold text-gray-900">{label}</span>
            <span className="block text-xs text-gray-500 font-medium uppercase tracking-wide">{subLabel}</span>
          </div>
        </div>
      </div>
      <div className="p-6 relative min-h-[100px] flex items-center justify-center">
        {isOpen ? (
          <div className="text-lg text-gray-700 leading-relaxed text-center animate-in fade-in duration-300">
            {content}
          </div>
        ) : (
          <>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full bg-gray-100/50 backdrop-blur-md absolute inset-0 z-10"></div>
              <button 
                onClick={onClick}
                className="z-20 flex items-center gap-2 px-6 py-3 bg-brand-blue text-white rounded-full font-bold shadow-lg hover:bg-blue-600 transition-all hover:scale-105"
              >
                <Eye className="w-4 h-4" />
                Unlock Hint
              </button>
            </div>
            <div className="text-lg text-gray-400 leading-relaxed text-center filter blur-sm select-none">
              {content.replace(/[a-zA-Z]/g, 'x')}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen pb-20">
      <SEO 
        title={`${clue.clue_text || `Clue ${clue.number}`} - Syllo Hint`}
        description={`Hints and answer for Syllo clue: ${clue.clue_text || `Clue ${clue.number}`}.`}
        breadcrumbs={breadcrumbs}
        noindex={true}
      />
      <Header date={puzzle.date} title={puzzle.title} gameName="Daily Syllo" />

      <div className="flex-grow max-w-3xl mx-auto px-4 w-full pt-6">
        {/* Navigation */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to={backLink}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-blue transition-colors font-medium text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Puzzle
          </Link>
        </div>

        {/* Main Clue Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border-2 border-gray-100 p-6 md:p-8 shadow-card mb-12"
        >
           <div className="flex items-center gap-3 mb-6">
             <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-600">
               Clue {clue.number}
             </span>
             <span className="text-gray-400 text-sm font-bold">{puzzle.title} • {new Date(puzzle.date).toLocaleDateString()}</span>
           </div>

           {/* In Syllo, the 'text' of the clue isn't explicitly mentioned in the schema as 'text'.
               The prompt says: "syllo_clues (this table will have ... answer and hint_1 ... and slug)".
               It doesn't explicitly mention 'text' or 'clue_text'.
               However, typical clues have text.
               Wait, "Merge syllables to solve clues. You get 5 clues to piece together 5 words".
               The schema I created: `syllo_clues`: `answer`, `hint_1`, `hint_2`, `hint_3`, `slug`, `number`.
               I missed `text` or `clue` column in the creation step?
               The user said: "syllo_clues (this table will have puzzle_date and number ... and answer and hint_1 and hint_2 and hint_3 and slug)".
               They did NOT mention a 'clue' text field.
               Maybe the hints *are* the clues? Or maybe I missed it.
               "Merge syllables to solve clues."
               If there is no clue text, what is displayed?
               "You get 5 clues to piece together 5 words".
               Maybe I should check if I missed 'text' or 'clue' in the requirement.
               "syllo_clues (this table will have puzzle_date and number ... and answer and hint_1 ... and slug)"
               It seems I followed instructions literally.
               However, usually a puzzle has a clue text.
               If I look at `ClueCardSimple`, it displays `clue.text`.
               If `syllo_clues` lacks `text`, what do I display?
               Maybe `hint_1` is the main clue?
               Or maybe I should add a `text` column.
               I'll assume `hint_1` acts as the clue text if there is no separate text, or maybe I should ask.
               But the user said "create these tables...".
               I'll assume `hint_1` is the "clue" or maybe I should have added `clue_text`.
               Actually, looking at `PuzzleContext`, `clues` table has `text`.
               For Syllo, the user listed fields explicitly.
               I'll assume for now that I might have missed `clue_text` or `hint_1` is the starting point.
               Let's look at `SylloContext.js` where I select columns:
               `id, number, answer, hint_1, hint_2, hint_3, slug`.
               If I don't have `text`, `ClueCardSimple` will crash or show nothing if I pass it `clue` object which expects `text`.
               `ClueCardSimple` uses `clue.text`.
               
               I should probably add `clue_text` to the table. It's safer.
               Or map `hint_1` to `text` in `SylloContext` if that's the intention.
               But `hint_1` is usually a hint.
               
               Let's re-read: "syllo_clues (this table will have puzzle_date and number ... and answer and hint_1 and hint_2 and hint_3 and slug)".
               It's possible the "clue" is just the "number" in some games, but "Merge syllables to solve clues" implies text.
               I will add `clue_text` to the table and Context. It's better to be safe.
               I will run a migration to add `clue_text`.
           */} 
           
           <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-8 leading-tight">
             {/* Fallback to hint_1 if no text, or just "Clue X" */}
             {clue.clue_text || `Clue ${clue.number}`}
           </h1>

           {/* Hints Section */}
           <div className="space-y-4 mb-12 entry-content">
             <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Progressive Hints</h2>
             
             {clue.hint_1 && (
               <HintItem 
                 label="Hint 1" 
                 subLabel="First Nudge"
                 icon={Lightbulb}
                 content={clue.hint_1}
                 isOpen={activeHint === 'hint1'}
                 onClick={() => toggleHint('hint1')}
               />
             )}
             
             {clue.hint_2 && (
               <HintItem 
                 label="Hint 2" 
                 subLabel="Getting Closer"
                 icon={Shuffle}
                 content={clue.hint_2}
                 isOpen={activeHint === 'hint2'}
                 onClick={() => toggleHint('hint2')}
               />
             )}
             
             {clue.hint_3 && (
               <HintItem 
                 label="Hint 3" 
                 subLabel="Almost There"
                 icon={HelpCircle}
                 content={clue.hint_3}
                 isOpen={activeHint === 'hint3'}
                 onClick={() => toggleHint('hint3')}
               />
             )}
           </div>

           <div className="text-center mb-8">
             <h2 className="text-2xl font-black text-gray-900 mb-2">
               Ready for the answer to this Syllo clue?
             </h2>
             <p className="text-gray-500">
               Before revealing the answer, make sure to check all the hints above! You can reveal the answer one letter at a time by clicking on the tiles, or reveal the entire word at once.
             </p>
           </div>

           {/* Tiles */}
           <div className="flex flex-wrap justify-center gap-2 mb-10">
             {tiles.map((_, index) => {
               const isRevealed = isAnswerRevealed || revealedTiles.includes(index);
               return (
                 <button 
                   key={index}
                   onClick={() => toggleTile(index)}
                   className={cn(
                     "w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg border-b-4 border-x border-t text-2xl md:text-3xl font-bold transition-all duration-300",
                     isRevealed 
                       ? "bg-brand-blue border-brand-blue border-b-blue-700 text-white shadow-lg transform translate-y-[2px] border-b-[2px] cursor-default" 
                       : "bg-white border-gray-200 border-b-gray-300 text-gray-300 hover:bg-gray-50 cursor-pointer"
                   )}
                 >
                   {isRevealed ? (
                     <motion.span
                       initial={{ opacity: 0, scale: 0.5 }}
                       animate={{ opacity: 1, scale: 1 }}
                     >
                       {clue.answer[index]}
                     </motion.span>
                   ) : (
                     <span className="opacity-10">?</span>
                   )}
                 </button>
               );
             })}
           </div>

           {/* Action Buttons */}
           <div className="flex justify-center mb-8">
             <button
               onClick={() => setIsAnswerRevealed(true)}
               disabled={isAnswerRevealed}
               className={cn(
                 "flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-lg font-bold transition-all shadow-sm border-2",
                 isAnswerRevealed
                   ? "bg-gray-100 text-gray-400 border-gray-100 cursor-default"
                   : "bg-gray-900 text-white border-gray-900 hover:bg-black hover:shadow-md"
               )}
             >
               <Eye className="w-5 h-5" />
               Reveal Full Answer
             </button>
           </div>
        </motion.div>

        {/* Related Clues */}
        <div className="pt-8 border-t border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Related Clues</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {relatedClues.map(related => (
              <Link 
                key={related.id}
                to={isToday ? `/todays-syllo-clues-answers/${related.slug}` : `/syllo/syllo-${puzzle.puzzleNumber}/${related.slug}`}
                className="block bg-white p-4 rounded-xl border border-gray-200 hover:border-brand-blue hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-0.5 rounded">
                    Clue {related.number}
                  </span>
                </div>
                <p className="font-medium text-gray-900 group-hover:text-brand-blue transition-colors">
                  {related.clue_text || `Clue ${related.number}`}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SylloCluePage;

